import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import SubmitButton from '../common/SubmitButton.jsx';
import { createLicenseFormFields } from '../../config/form-fields.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import createLicenseValidation from '../../validation/create-ts-plus-license-validations.js';
import useTsPlusLicenses from '../../hooks/useTsPlusLicenses.js';

const formatYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().substring(0, 10); // yyyy-mm-dd
};

const badge = (status) => {
    const map = {
        ACTIVE: { bg: '#22c55e', fg: '#fff', label: 'Active' },
        AVAILABLE: { bg: '#eab308', fg: '#000', label: 'Available' },
        WAITING: { bg: '#3b82f6', fg: '#fff', label: 'Waiting' },
        ERROR: { bg: '#ef4444', fg: '#fff', label: 'Error' }
    };
    const s = map[status] || { bg: '#9ca3af', fg: '#fff' };
    return (
        <span
            style={{
                background: s.bg,
                color: s.fg,
                padding: '2px 8px',
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                marginLeft: 8
            }}>
            {status}
        </span>
    );
};

/**
 * CreateUpdateTsPlusLicenseForm component for rendering the form to create a new license.
 *
 * @component
 * @returns {JSX.Element} - The rendered CreateUpdateTsPlusLicenseForm component.
 */
const CreateUpdateTsPlusLicenseForm = ({ objectToUpdate }) => {
    const { error, loading, createLicense, updateLicense, license } =
        useTsPlusLicenses();

    const base = objectToUpdate || createLicenseFormFields;
    const initialValues = {
        ...base,
        errorFlag: typeof base?.errorFlag === 'boolean' ? base.errorFlag : false
    };

    const formik = useFormik({
        initialValues,
        validationSchema: createLicenseValidation,
        onSubmit: async (values) => {
            // Clean & normalize payload
            const payload = { ...values };

            // Normalize computerId empty -> null
            if (payload.computerId === '') payload.computerId = null;

            if (!objectToUpdate) {
                payload.createDate = payload.createDate
                    ? new Date(payload.createDate)
                    : new Date();
            } else {
                // ensure we don't send createDate in update payload (server authoritative)
                delete payload.createDate;
            }

            payload.rehostDate = payload.rehostDate
                ? new Date(payload.rehostDate)
                : null;

            // Strip fields the server shouldn’t receive
            delete payload._id;
            delete payload.id;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.status; // derived only
            // NOTE: keep errorFlag (new field you added)

            if (objectToUpdate) {
                await updateLicense(objectToUpdate._id, payload);
            } else {
                await createLicense(payload);
            }
        }
    });

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="newsc-form-container">
                    <label htmlFor="license" className="form-label">
                        License{' '}
                        {objectToUpdate?.status && badge(objectToUpdate.status)}
                    </label>
                    <input
                        id="license"
                        name="license"
                        type="text"
                        className="newsc-input"
                        placeholder="e.g. XXXX-XXXX-XXXX-XXXX"
                        {...formik.getFieldProps('license')}
                    />
                    {formik.touched.license && formik.errors.license ? (
                        <ErrorMessage message={formik.errors.license} />
                    ) : null}
                    {/* <label htmlFor="officeName" className="form-label">
                        Office Name
                    </label>
                    <input
                        id="officeName"
                        name="officeName"
                        type="text"
                        className="newsc-input"
                        placeholder="Enter office name"
                        disabled={
                            !!(objectToUpdate && objectToUpdate.officeName)
                        }
                        {...formik.getFieldProps('officeName')}
                    />
                    {formik.touched.officeName && formik.errors.officeName ? (
                        <ErrorMessage message={formik.errors.officeName} />
                    ) : null}

                    <label htmlFor="computerDetails" className="form-label">
                        Computer Details
                    </label>
                    <input
                        id="computerDetails"
                        name="computerDetails"
                        type="text"
                        className="newsc-input"
                        placeholder="Enter computer details"
                        disabled={
                            !!(objectToUpdate && objectToUpdate.computerDetails)
                        }
                        {...formik.getFieldProps('computerDetails')}
                    />
                    {formik.touched.computerDetails &&
                    formik.errors.computerDetails ? (
                        <ErrorMessage message={formik.errors.computerDetails} />
                    ) : null} */}

                    <label htmlFor="numOfUsers" className="form-label">
                        # of Users
                    </label>
                    <input
                        id="numOfUsers"
                        name="numOfUsers"
                        type="number"
                        className="newsc-input"
                        {...formik.getFieldProps('numOfUsers')}
                    />
                    {formik.touched.numOfUsers && formik.errors.numOfUsers ? (
                        <ErrorMessage message={formik.errors.numOfUsers} />
                    ) : null}

                    <label htmlFor="createDate" className="form-label">
                        Create Date
                    </label>
                    <input
                        id="createDate"
                        name="createDate"
                        type="date"
                        className="newsc-input"
                        value={formatYMD(formik.values.createDate)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.createDate && formik.errors.createDate ? (
                        <ErrorMessage message={formik.errors.createDate} />
                    ) : null}

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 12
                        }}>
                        <input
                            id="errorFlag"
                            name="errorFlag"
                            type="checkbox"
                            checked={!!formik.values.errorFlag}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'errorFlag',
                                    e.target.checked
                                )
                            }
                        />
                        <label
                            htmlFor="errorFlag"
                            className="form-label"
                            style={{ margin: 0 }}>
                            Mark as Error
                        </label>
                    </div>
                    {objectToUpdate ? (
                        <SubmitButton label="Save" />
                    ) : (
                        <SubmitButton label="Create" />
                    )}
                </div>
            </form>
            <br />
            <br />
            {loading && <LoadingSpinner />}
            {error && <ErrorMessage message={error.message} />}
            {license && objectToUpdate && (
                <SuccessMessage message="New license updated successfully!" />
            )}
            {license && !objectToUpdate && (
                <SuccessMessage message="New license created and added successfully!" />
            )}
        </>
    );
};

CreateUpdateTsPlusLicenseForm.propTypes = {
    objectToUpdate: PropTypes.object
};

export default CreateUpdateTsPlusLicenseForm;
